import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Users, Search, Filter, ChevronRight, RefreshCw, UserPlus, Calendar,
  CheckCircle, Clock, CreditCard, AlertCircle, Mail, Phone, Eye,
  ChevronDown, ChevronUp, UserCheck, XCircle, Loader2, DollarSign, Trash2
} from 'lucide-react';

/**
 * RegistrationProgressTab - Unified Admin View of All User Registrations
 * 
 * Shows signup_status, registration_progress, and payment_status for:
 * - Trial users (booked, attended, converted)
 * - Admin-imported students
 * 
 * Provides clear visibility into who has signed up, who has completed 
 * registration, who has added payment details, and which payments 
 * require authorization.
 */

const STATUS_COLORS = {
  // Signup Status
  trial_booked: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  trial_attended: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  trial_no_show: 'bg-red-500/20 text-red-300 border-red-500/30',
  converted: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  imported: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  
  // Registration Progress
  not_started: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  
  // Payment Status
  no_card: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  card_added: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  payment_pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  payment_authorized: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  payment_failed: 'bg-red-500/20 text-red-300 border-red-500/30',
  paid: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
};

const STATUS_LABELS = {
  // Signup Status
  trial_booked: 'Trial Booked',
  trial_attended: 'Trial Attended',
  trial_no_show: 'Trial No Show',
  converted: 'Converted',
  imported: 'Admin Imported',
  
  // Registration Progress
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  
  // Payment Status
  no_card: 'No Card Added',
  card_added: 'Card Added',
  payment_pending: 'Pending Authorization',
  payment_authorized: 'Authorized',
  payment_failed: 'Failed',
  paid: 'Paid'
};

const RegistrationProgressTab = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Data
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    trialBooked: 0,
    trialAttended: 0,
    converted: 0,
    imported: 0,
    registrationComplete: 0,
    cardAdded: 0,
    pendingAuthorization: 0,
    paid: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch trials
      const trialsRes = await api.get('/free-trials');
      const trials = trialsRes.data || [];
      
      // Fetch bookings with payment status
      const bookingsRes = await api.get('/bookings');
      const bookings = bookingsRes.data || [];
      
      // Fetch pending enrollments (post-trial)
      let pendingEnrollments = [];
      try {
        const pendingRes = await api.get('/trials/pending-enrollments');
        pendingEnrollments = pendingRes.data || [];
      } catch (e) {
        // Endpoint might not exist
      }
      
      // Fetch imported students
      let importedStudents = [];
      try {
        const importedRes = await api.get('/students/imported');
        importedStudents = importedRes.data || [];
      } catch (e) {
        // Endpoint might not exist
      }
      
      // Combine and normalize data
      const normalizedData = normalizeRegistrations(trials, bookings, pendingEnrollments, importedStudents);
      setRegistrations(normalizedData);
      
      // Calculate stats
      calculateStats(normalizedData);
      
    } catch (error) {
      console.error('Failed to fetch registration data:', error);
      toast.error('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  };

  const normalizeRegistrations = (trials, bookings, pendingEnrollments, importedStudents) => {
    const normalized = [];
    
    // Process trials
    trials.forEach(trial => {
      const hasBooking = bookings.find(b => 
        b.student_name?.toLowerCase() === `${trial.student_first_name} ${trial.student_last_name}`.toLowerCase() ||
        b.parent_email === trial.parent_email
      );
      
      const pendingEnrollment = pendingEnrollments.find(p => p.trial_id === trial.id);
      
      // Determine signup status
      let signupStatus = 'trial_booked';
      if (trial.attendance === 'attended') {
        signupStatus = hasBooking?.payment_status === 'paid' ? 'converted' : 'trial_attended';
      } else if (trial.attendance === 'no_show') {
        signupStatus = 'trial_no_show';
      } else if (trial.converted) {
        signupStatus = 'converted';
      }
      
      // Determine registration progress
      let registrationProgress = 'not_started';
      if (pendingEnrollment) {
        registrationProgress = pendingEnrollment.status === 'completed' ? 'completed' : 'in_progress';
      } else if (hasBooking) {
        registrationProgress = 'completed';
      }
      
      // Determine payment status
      let paymentStatus = 'no_card';
      if (hasBooking) {
        if (hasBooking.payment_status === 'paid') {
          paymentStatus = 'paid';
        } else if (hasBooking.payment_status === 'pending') {
          paymentStatus = hasBooking.has_card ? 'payment_pending' : 'no_card';
        } else if (hasBooking.payment_status === 'failed') {
          paymentStatus = 'payment_failed';
        }
      } else if (pendingEnrollment?.payment_status === 'card_saved') {
        paymentStatus = 'card_added';
      }
      
      normalized.push({
        id: trial.id,
        type: 'trial',
        studentName: `${trial.student_first_name || ''} ${trial.student_last_name || ''}`.trim(),
        parentName: trial.parent_name || '',
        parentEmail: trial.parent_email || '',
        parentPhone: trial.parent_phone || '',
        trialDate: trial.trial_date,
        classInterest: trial.class_interest,
        createdAt: trial.created_at,
        signupStatus,
        registrationProgress,
        paymentStatus,
        bookingId: hasBooking?.id,
        amount: hasBooking?.amount || pendingEnrollment?.total_amount || 0,
        hasCard: hasBooking?.has_card || pendingEnrollment?.payment_status === 'card_saved',
        // Email tracking
        reminderSent: !!trial.reminder_sent_at,
        followupSent: !!trial.followup_sent_at || !!trial.auto_followup_sent
      });
    });
    
    // Process imported students that aren't from trials
    importedStudents.forEach(student => {
      const alreadyIncluded = normalized.find(n => 
        n.studentName.toLowerCase() === student.student_name?.toLowerCase()
      );
      
      if (!alreadyIncluded) {
        normalized.push({
          id: student.id,
          type: 'imported',
          studentName: student.student_name || '',
          parentName: student.guardian1_name || '',
          parentEmail: student.guardian1_email || '',
          parentPhone: student.guardian1_phone || '',
          createdAt: student.created_at,
          signupStatus: 'imported',
          registrationProgress: student.registration_complete ? 'completed' : 'not_started',
          paymentStatus: student.has_card ? 'card_added' : 'no_card',
          hasCard: student.has_card || false,
          amount: 0
        });
      }
    });
    
    // Sort by created_at descending
    normalized.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    
    return normalized;
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      trialBooked: data.filter(d => d.signupStatus === 'trial_booked').length,
      trialAttended: data.filter(d => d.signupStatus === 'trial_attended').length,
      converted: data.filter(d => d.signupStatus === 'converted').length,
      imported: data.filter(d => d.signupStatus === 'imported').length,
      registrationComplete: data.filter(d => d.registrationProgress === 'completed').length,
      cardAdded: data.filter(d => ['card_added', 'payment_pending', 'paid'].includes(d.paymentStatus)).length,
      pendingAuthorization: data.filter(d => d.paymentStatus === 'payment_pending').length,
      paid: data.filter(d => d.paymentStatus === 'paid').length
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Delete registration
  const handleDeleteRegistration = async (reg) => {
    if (!window.confirm(`Are you sure you want to delete the registration for ${reg.studentName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      if (reg.type === 'trial') {
        // Delete trial
        await api.delete(`/free-trials/${reg.id}`);
      } else if (reg.type === 'imported') {
        // Delete imported student
        await api.delete(`/students/imported/${reg.id}`);
      }
      toast.success('Registration deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete registration');
    }
  };

  // Filter logic
  const filteredData = useMemo(() => {
    let filtered = registrations;
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.studentName?.toLowerCase().includes(term) ||
        r.parentName?.toLowerCase().includes(term) ||
        r.parentEmail?.toLowerCase().includes(term)
      );
    }
    
    // Apply filter
    switch (activeFilter) {
      case 'trial_booked':
        filtered = filtered.filter(r => r.signupStatus === 'trial_booked');
        break;
      case 'trial_attended':
        filtered = filtered.filter(r => r.signupStatus === 'trial_attended');
        break;
      case 'converted':
        filtered = filtered.filter(r => r.signupStatus === 'converted');
        break;
      case 'imported':
        filtered = filtered.filter(r => r.signupStatus === 'imported');
        break;
      case 'pending_authorization':
        filtered = filtered.filter(r => r.paymentStatus === 'payment_pending');
        break;
      case 'no_card':
        filtered = filtered.filter(r => r.paymentStatus === 'no_card' && r.registrationProgress !== 'not_started');
        break;
      case 'registration_incomplete':
        filtered = filtered.filter(r => r.registrationProgress === 'in_progress');
        break;
      default:
        break;
    }
    
    return filtered;
  }, [registrations, searchTerm, activeFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats Cards
  const StatCard = ({ label, count, color, icon: Icon, filterKey, isActive }) => (
    <button
      onClick={() => setActiveFilter(filterKey === activeFilter ? 'all' : filterKey)}
      className={`p-4 rounded-lg border transition-all text-left w-full ${
        isActive 
          ? 'ring-2 ring-[#FF007F] border-[#FF007F] bg-[#FF007F]/10' 
          : 'border-[#333] bg-[#111] hover:border-[#555]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-white/50">{label}</p>
          <p className={`text-2xl font-bold ${color}`}>{count}</p>
        </div>
        <Icon className={`w-6 h-6 ${color} opacity-50`} />
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registration-progress-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">Registration Progress</h2>
          <p className="text-sm text-white/50">Track all signups, registrations, and payment status</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="border-[#333] text-white hover:bg-[#222]"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <StatCard
          label="Total Registrations"
          count={stats.total}
          color="text-white"
          icon={Users}
          filterKey="all"
          isActive={activeFilter === 'all'}
        />
        <StatCard
          label="Trials Booked"
          count={stats.trialBooked}
          color="text-blue-400"
          icon={Calendar}
          filterKey="trial_booked"
          isActive={activeFilter === 'trial_booked'}
        />
        <StatCard
          label="Trials Attended"
          count={stats.trialAttended}
          color="text-emerald-400"
          icon={UserCheck}
          filterKey="trial_attended"
          isActive={activeFilter === 'trial_attended'}
        />
        <StatCard
          label="Converted"
          count={stats.converted}
          color="text-purple-400"
          icon={CheckCircle}
          filterKey="converted"
          isActive={activeFilter === 'converted'}
        />
        <StatCard
          label="Pending Authorization"
          count={stats.pendingAuthorization}
          color="text-yellow-400"
          icon={Clock}
          filterKey="pending_authorization"
          isActive={activeFilter === 'pending_authorization'}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border border-[#333] bg-[#111]">
          <p className="text-xs text-white/50">Admin Imported</p>
          <p className="text-xl font-semibold text-amber-400">{stats.imported}</p>
        </div>
        <div className="p-3 rounded-lg border border-[#333] bg-[#111]">
          <p className="text-xs text-white/50">Registration Complete</p>
          <p className="text-xl font-semibold text-emerald-400">{stats.registrationComplete}</p>
        </div>
        <div className="p-3 rounded-lg border border-[#333] bg-[#111]">
          <p className="text-xs text-white/50">Cards on File</p>
          <p className="text-xl font-semibold text-blue-400">{stats.cardAdded}</p>
        </div>
        <div className="p-3 rounded-lg border border-[#333] bg-[#111]">
          <p className="text-xs text-white/50">Paid</p>
          <p className="text-xl font-semibold text-emerald-400">{stats.paid}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#111] border-[#333] text-white"
          />
        </div>
        {activeFilter !== 'all' && (
          <Button
            variant="outline"
            onClick={() => setActiveFilter('all')}
            className="border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/10"
          >
            Clear Filter
          </Button>
        )}
      </div>

      {/* Results Table */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF007F]" />
            Registrations ({filteredData.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredData.length === 0 ? (
            <div className="py-12 text-center text-white/50">
              <Users className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No registrations found</p>
            </div>
          ) : (
            <div className="divide-y divide-[#333]">
              {filteredData.map(reg => (
                <div key={reg.id} className="hover:bg-[#1a1a1a] transition-colors">
                  {/* Main Row */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleExpand(reg.id)}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-[#FF007F]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#FF007F] font-bold">
                          {reg.studentName?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{reg.studentName || 'Unknown'}</p>
                        <p className="text-sm text-white/50 truncate">{reg.parentEmail}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Signup Status */}
                      <Badge className={STATUS_COLORS[reg.signupStatus] || STATUS_COLORS.not_started}>
                        {STATUS_LABELS[reg.signupStatus] || reg.signupStatus}
                      </Badge>
                      
                      {/* Registration Progress */}
                      <Badge className={STATUS_COLORS[reg.registrationProgress] || STATUS_COLORS.not_started}>
                        {STATUS_LABELS[reg.registrationProgress] || 'Unknown'}
                      </Badge>
                      
                      {/* Payment Status */}
                      <Badge className={STATUS_COLORS[reg.paymentStatus] || STATUS_COLORS.no_card}>
                        {STATUS_LABELS[reg.paymentStatus] || 'Unknown'}
                      </Badge>
                      
                      {/* Expand Icon */}
                      {expandedRows.has(reg.id) ? (
                        <ChevronUp className="w-5 h-5 text-white/50" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/50" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedRows.has(reg.id) && (
                    <div className="px-4 pb-4 pt-0 bg-[#0a0a0a] border-t border-[#333]">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
                        {/* Contact Info */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-white/70">Contact Details</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-white/50 flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              {reg.parentName || 'N/A'}
                            </p>
                            <a href={`mailto:${reg.parentEmail}`} className="text-blue-400 flex items-center gap-2 hover:underline">
                              <Mail className="w-4 h-4" />
                              {reg.parentEmail || 'N/A'}
                            </a>
                            {reg.parentPhone && (
                              <a href={`tel:${reg.parentPhone}`} className="text-green-400 flex items-center gap-2 hover:underline">
                                <Phone className="w-4 h-4" />
                                {reg.parentPhone}
                              </a>
                            )}
                          </div>
                        </div>
                        
                        {/* Timeline */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-white/70">Timeline</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-white/50">
                              Created: {formatDate(reg.createdAt)}
                            </p>
                            {reg.trialDate && (
                              <p className="text-white/50">
                                Trial: {formatDate(reg.trialDate)}
                              </p>
                            )}
                            {reg.classInterest && (
                              <p className="text-white/50">
                                Interest: <span className="text-[#FF007F]">{reg.classInterest}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Payment Info */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-white/70">Payment</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-white/50 flex items-center gap-2">
                              <CreditCard className="w-4 h-4" />
                              Card: {reg.hasCard ? <span className="text-emerald-400">On file</span> : <span className="text-red-400">Not added</span>}
                            </p>
                            {reg.amount > 0 && (
                              <p className="text-white flex items-center gap-2">
                                <DollarSign className="w-4 h-4" />
                                Amount: £{reg.amount.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Email Status for Trials */}
                      {reg.type === 'trial' && (
                        <div className="flex items-center gap-4 pt-2 border-t border-[#333]">
                          <span className="text-xs text-white/50">Emails:</span>
                          <span className={`text-xs ${reg.reminderSent ? 'text-emerald-400' : 'text-white/30'}`}>
                            {reg.reminderSent ? '✓' : '○'} Reminder
                          </span>
                          <span className={`text-xs ${reg.followupSent ? 'text-emerald-400' : 'text-white/30'}`}>
                            {reg.followupSent ? '✓' : '○'} Follow-up
                          </span>
                        </div>
                      )}
                      
                      {/* Delete Button */}
                      <div className="flex justify-end pt-3 border-t border-[#333] mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRegistration(reg);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Registration
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationProgressTab;
